import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Button } from "../../components/ui/button";
import { Switch } from "../../components/ui/switch";
import { toast } from "sonner";
import { CheckCircle2, AlertTriangle, ImagePlus } from "lucide-react";
import { uploadImage } from "../../components/admin/ImageUpload";

const TABS = ["General", "Payment", "Marketing", "SEO", "Legal"];

export default function AdminSettings() {
  const [tab, setTab] = useState("General");
  return (
    <div>
      <h1 className="font-heading text-2xl font-bold mb-6">Settings & SEO</h1>
      <div className="flex gap-1 mb-6 border-b border-line overflow-x-auto">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)} className={`px-4 py-2.5 text-sm border-b-2 -mb-px whitespace-nowrap ${tab === t ? "border-ember text-ember" : "border-transparent text-content-secondary"}`} data-testid={`settings-tab-${t.toLowerCase()}`}>{t}</button>
        ))}
      </div>
      {tab === "General" && <General />}
      {tab === "Payment" && <Payment />}
      {tab === "Marketing" && <Marketing />}
      {tab === "SEO" && <Seo />}
      {tab === "Legal" && <Legal />}
    </div>
  );
}

function useDoc(getUrl, putUrl) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(getUrl).then((r) => setD(r.data || {})).catch(() => setD({})); }, [getUrl]);
  const save = async (payload) => {
    setBusy(true);
    try { const { data } = await api.put(putUrl, payload ?? d); setD(data); toast.success("Saved"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return { d, setD, save, busy };
}

const F = ({ label, children, full }) => (<div className={full ? "sm:col-span-2" : ""}><label className="text-sm text-content-secondary">{label}</label><div className="mt-1">{children}</div></div>);

function General() {
  const { d, setD, save, busy } = useDoc("/admin/site-settings", "/admin/site-settings");
  if (!d) return null;
  const set = (k, v) => setD({ ...d, [k]: v });
  return (
    <div className="card-surface p-6 grid sm:grid-cols-2 gap-4 max-w-3xl">
      <F label="Brand name"><Input value={d.brand_name || ""} onChange={(e) => set("brand_name", e.target.value)} className="bg-ink border-line" data-testid="set-brand" /></F>
      <F label="Tagline"><Input value={d.tagline || ""} onChange={(e) => set("tagline", e.target.value)} className="bg-ink border-line" /></F>
      <F label="Support email"><Input value={d.email || ""} onChange={(e) => set("email", e.target.value)} className="bg-ink border-line" /></F>
      <F label="WhatsApp number"><Input value={d.whatsapp || ""} onChange={(e) => set("whatsapp", e.target.value)} className="bg-ink border-line" data-testid="set-whatsapp" /></F>
      <F label="Support hours"><Input value={d.support_hours || ""} onChange={(e) => set("support_hours", e.target.value)} className="bg-ink border-line" /></F>
      <F label="Hero headline" full><Input value={d.hero_headline || ""} onChange={(e) => set("hero_headline", e.target.value)} className="bg-ink border-line" /></F>
      <F label="Hero subtext" full><Textarea value={d.hero_subtext || ""} onChange={(e) => set("hero_subtext", e.target.value)} className="bg-ink border-line" /></F>
      <F label="About content (HTML)" full><Textarea rows={5} value={d.about_content || ""} onChange={(e) => set("about_content", e.target.value)} className="bg-ink border-line" /></F>
      <div className="sm:col-span-2"><Button className="btn-ember" onClick={() => save()} disabled={busy} data-testid="set-general-save">Save</Button></div>
    </div>
  );
}

function Payment() {
  const { d, setD, save, busy } = useDoc("/admin/payment-settings", "/admin/payment-settings");
  if (!d) return null;
  const set = (k, v) => setD({ ...d, [k]: v });
  const Configured = ({ ok, name }) => (
    <div className={`flex items-center gap-2 text-sm ${ok ? "text-emerald-400" : "text-amber-400"}`}>
      {ok ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
      {name} keys {ok ? "configured" : "not set (test/simulation mode)"}
    </div>
  );
  return (
    <div className="card-surface p-6 space-y-5 max-w-2xl">
      <div className="bg-ink rounded-lg p-4 space-y-2">
        <Configured ok={d.razorpay_configured} name="Razorpay" />
        <Configured ok={d.cashfree_configured} name="Cashfree" />
        <p className="text-xs text-content-muted mt-2">Add secret keys to <span className="font-mono">backend/.env</span> (RAZORPAY_KEY_ID, RAZORPAY_KEY_SECRET) to enable live checkout. Until then, checkout runs in a safe test mode. Secret keys are never exposed to the frontend.</p>
      </div>
      <label className="flex items-center gap-2 text-sm"><Switch checked={!!d.razorpay_enabled} onCheckedChange={(v) => set("razorpay_enabled", v)} data-testid="pay-razorpay" /> Enable Razorpay</label>
      <label className="flex items-center gap-2 text-sm"><Switch checked={!!d.cashfree_enabled} onCheckedChange={(v) => set("cashfree_enabled", v)} data-testid="pay-cashfree" /> Enable Cashfree</label>
      <F label="Default gateway">
        <select value={d.default_gateway || "razorpay"} onChange={(e) => set("default_gateway", e.target.value)} className="w-full h-10 rounded-md bg-ink border border-line px-3 text-sm max-w-xs">
          <option value="razorpay">Razorpay</option><option value="cashfree">Cashfree</option>
        </select>
      </F>
      <Button className="btn-ember" onClick={() => save({ razorpay_enabled: d.razorpay_enabled, cashfree_enabled: d.cashfree_enabled, default_gateway: d.default_gateway })} disabled={busy} data-testid="pay-save">Save</Button>
    </div>
  );
}

function Marketing() {
  const { d, setD, save, busy } = useDoc("/admin/tracking-settings", "/admin/tracking-settings");
  if (!d) return null;
  const set = (k, v) => setD({ ...d, [k]: v });
  return (
    <div className="card-surface p-6 grid sm:grid-cols-2 gap-4 max-w-3xl">
      <label className="flex items-center gap-2 text-sm sm:col-span-2"><Switch checked={d.enabled !== false} onCheckedChange={(v) => set("enabled", v)} data-testid="track-enabled" /> Enable tracking</label>
      <F label="Meta Pixel ID"><Input value={d.meta_pixel_id || ""} onChange={(e) => set("meta_pixel_id", e.target.value)} className="bg-ink border-line" data-testid="track-meta" /></F>
      <F label="GA4 Measurement ID"><Input value={d.ga4_id || ""} onChange={(e) => set("ga4_id", e.target.value)} className="bg-ink border-line" data-testid="track-ga4" /></F>
      <F label="Google Tag Manager ID"><Input value={d.gtm_id || ""} onChange={(e) => set("gtm_id", e.target.value)} className="bg-ink border-line" /></F>
      <F label="Google Ads Conversion ID"><Input value={d.google_ads_id || ""} onChange={(e) => set("google_ads_id", e.target.value)} className="bg-ink border-line" /></F>
      <F label="Google Ads Conversion Label"><Input value={d.google_ads_label || ""} onChange={(e) => set("google_ads_label", e.target.value)} className="bg-ink border-line" /></F>
      <div className="sm:col-span-2 bg-ink rounded-lg p-3 text-xs text-amber-400 flex gap-2"><AlertTriangle className="w-4 h-4 shrink-0" /> Custom scripts run in visitors' browsers. Only paste trusted code.</div>
      <F label="Custom HEAD scripts" full><Textarea rows={3} value={d.head_scripts || ""} onChange={(e) => set("head_scripts", e.target.value)} className="bg-ink border-line font-mono text-xs" /></F>
      <F label="Custom FOOTER scripts" full><Textarea rows={3} value={d.footer_scripts || ""} onChange={(e) => set("footer_scripts", e.target.value)} className="bg-ink border-line font-mono text-xs" /></F>
      <div className="sm:col-span-2"><Button className="btn-ember" onClick={() => save()} disabled={busy} data-testid="track-save">Save</Button></div>
    </div>
  );
}

function Seo() {
  const [list, setList] = useState([]);
  const [sel, setSel] = useState(null);
  const [form, setForm] = useState({});
  useEffect(() => { api.get("/admin/seo").then((r) => setList(r.data)).catch(() => {}); }, []);
  const load = (path) => { const e = list.find((x) => x.path === path); setForm(e || { path }); setSel(path); };
  const save = async () => {
    try { await api.put(`/admin/seo/${form.path}`, form); toast.success("SEO saved"); const { data } = await api.get("/admin/seo"); setList(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const paths = ["global", "home", ...list.map((l) => l.path)].filter((v, i, a) => a.indexOf(v) === i);
  return (
    <div className="grid sm:grid-cols-3 gap-4">
      <div className="card-surface p-4 space-y-1">
        <div className="text-xs text-content-muted mb-2">Pages</div>
        {paths.map((p) => <button key={p} onClick={() => load(p)} className={`block w-full text-left px-3 py-2 rounded-lg text-sm ${sel === p ? "bg-ember-soft text-ember" : "hover:bg-card-hover"}`} data-testid={`seo-page-${p}`}>{p}</button>)}
        <input placeholder="new path e.g. ssc" className="w-full mt-2 h-9 bg-ink border border-line rounded-md px-2 text-sm" onKeyDown={(e) => { if (e.key === "Enter") load(e.target.value); }} data-testid="seo-new-path" />
      </div>
      <div className="sm:col-span-2 card-surface p-6 space-y-3">
        {sel ? (
          <>
            <div className="text-sm font-medium">Editing: <span className="font-mono text-ember">{form.path}</span></div>
            <F label="SEO title" full><Input value={form.seo_title || ""} onChange={(e) => setForm({ ...form, seo_title: e.target.value })} className="bg-ink border-line" data-testid="seo-title" /></F>
            <F label="Meta description" full><Textarea value={form.meta_description || ""} onChange={(e) => setForm({ ...form, meta_description: e.target.value })} className="bg-ink border-line" /></F>
            <F label="Canonical URL" full><Input value={form.canonical || ""} onChange={(e) => setForm({ ...form, canonical: e.target.value })} className="bg-ink border-line" /></F>
            <F label="OG image URL" full><Input value={form.og_image || ""} onChange={(e) => setForm({ ...form, og_image: e.target.value })} className="bg-ink border-line" /></F>
            <F label="Robots" full><Input value={form.robots || "index,follow"} onChange={(e) => setForm({ ...form, robots: e.target.value })} className="bg-ink border-line" /></F>
            <F label="Schema JSON-LD" full><Textarea rows={4} value={form.schema || ""} onChange={(e) => setForm({ ...form, schema: e.target.value })} className="bg-ink border-line font-mono text-xs" /></F>
            <div className="bg-ink rounded-lg p-3"><div className="text-ember text-sm">{form.seo_title || "SEO title preview"}</div><div className="text-emerald-500 text-xs">agnishiksha.in/{form.path}</div><div className="text-xs text-content-secondary">{form.meta_description || "Meta description preview…"}</div></div>
            <Button className="btn-ember" onClick={save} data-testid="seo-save">Save SEO</Button>
          </>
        ) : <div className="text-sm text-content-muted">Select a page to edit its SEO.</div>}
      </div>
    </div>
  );
}

function Legal() {
  const [list, setList] = useState([]);
  const [sel, setSel] = useState(null);
  const [form, setForm] = useState({});
  useEffect(() => { api.get("/admin/legal").then((r) => setList(r.data)).catch(() => {}); }, []);
  const save = async () => {
    try { await api.put(`/admin/legal/${form.slug}`, form); toast.success("Saved"); const { data } = await api.get("/admin/legal"); setList(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const imgRef = React.useRef(null);
  const insertImg = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    try { const url = await uploadImage(file); setForm((f) => ({ ...f, content: `${f.content || ""}\n<img src="${url}" alt="" />\n` })); toast.success("Image inserted"); }
    catch (err) { toast.error("Upload failed"); } finally { if (imgRef.current) imgRef.current.value = ""; }
  };
  return (
    <div className="grid sm:grid-cols-3 gap-4">
      <div className="card-surface p-4 space-y-1">
        {list.map((l) => <button key={l.slug} onClick={() => { setForm(l); setSel(l.slug); }} className={`block w-full text-left px-3 py-2 rounded-lg text-sm ${sel === l.slug ? "bg-ember-soft text-ember" : "hover:bg-card-hover"}`} data-testid={`legal-${l.slug}`}>{l.title}</button>)}
      </div>
      <div className="sm:col-span-2 card-surface p-6 space-y-3">
        {sel ? (<>
          <F label="Title" full><Input value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} className="bg-ink border-line" /></F>
          <F label="Content (HTML)" full><Textarea rows={12} value={form.content || ""} onChange={(e) => setForm({ ...form, content: e.target.value })} className="bg-ink border-line font-mono text-xs" data-testid="legal-content" /></F>
          <input ref={imgRef} type="file" accept="image/*" className="hidden" onChange={insertImg} data-testid="legal-img-input" />
          <button type="button" onClick={() => imgRef.current?.click()} className="inline-flex items-center gap-1.5 text-xs text-ember hover:underline" data-testid="legal-insert-image"><ImagePlus className="w-3.5 h-3.5" /> Upload & insert image</button>
          <div><Button className="btn-ember" onClick={save} data-testid="legal-save">Save</Button></div>
        </>) : <div className="text-sm text-content-muted">Select a legal page to edit.</div>}
      </div>
    </div>
  );
}
